import { Typography, Autocomplete } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import TextField from 'components/text-field';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { listOrders } from 'graphql/orders';
import type { OrderListingType } from 'types/order';
import { autocompleteRootProps } from 'v2-utils/autocomplete';

const SEARCH_MIN_CHARS = 2;
const SEARCH_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

export type PickedOrder = { _id: string; order_id: string };

const useStyles = makeStyles((theme: Theme) => createStyles({
  status: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  error: {
    marginTop: theme.spacing(1),
    color: theme.palette.error.dark,
  },
}));

const useDebouncedValue = (value: string, delayMs: number): string => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
};

type ExistingOrderSearchProps = {
  /** Current order id from the markdown target line, shown until the user picks another order. */
  targetId: string;
  agencyId?: string;
  onSelect: (order: PickedOrder) => void;
};

export const ExistingOrderSearch = ({ targetId, agencyId, onSelect }: ExistingOrderSearchProps) => {
  const classes = useStyles();
  const { t } = useTranslation('imports');
  const [inputText, setInputText] = useState(targetId);
  const searchText = useDebouncedValue(inputText.trim(), SEARCH_DEBOUNCE_MS);
  const enabled = searchText.length >= SEARCH_MIN_CHARS;

  const { data, isFetching, isError } = useQuery(
    ['imports-order-search', searchText, agencyId ?? ''],
    () => listOrders({
      pagination: { items: SEARCH_PAGE_SIZE, page: 1 },
      searchText,
      agencyId,
    }),
    { enabled, keepPreviousData: true, refetchOnWindowFocus: false },
  );

  const options: OrderListingType[] = enabled ? (data?.listOrders.orders ?? []) : [];

  return (
    <>
      <Autocomplete<OrderListingType, false, false, false>
        fullWidth
        options={options}
        filterOptions={(list) => list}
        getOptionLabel={(order) => order.order_id || order._id}
        value={null}
        inputValue={inputText}
        onInputChange={(_, value, reason) => {
          if (reason === 'input' || reason === 'clear') setInputText(value);
        }}
        onChange={(_, order) => {
          if (!order) return;
          setInputText(order.order_id || order._id);
          onSelect({ _id: order._id, order_id: order.order_id });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={params.InputProps.ref}
            InputProps={autocompleteRootProps(params.inputProps)}
            label={t('imports:target.searchOrder')}
          />
        )}
      />
      {!enabled && (
        <Typography variant="caption" display="block" className={classes.status}>
          {t('imports:target.searchHint')}
        </Typography>
      )}
      {enabled && isFetching && (
        <Typography variant="caption" display="block" className={classes.status}>
          {t('imports:target.searching')}
        </Typography>
      )}
      {enabled && !isFetching && !isError && options.length === 0 && (
        <Typography variant="caption" display="block" className={classes.status}>
          {t('imports:target.noResults')}
        </Typography>
      )}
      {isError && (
        <Typography variant="caption" display="block" className={classes.error}>
          {t('imports:target.searchError')}
        </Typography>
      )}
    </>
  );
};
